import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Formik, FormikProps } from 'formik';
import { Dayparts as DaypartsValue } from 'core/dayparts/Dayparts';
import { Dayparts } from 'components/Dayparts/Dayparts';
import i18n from 'i18n';
import {
  countSelectedHours,
  DAYPART_DAYS,
  DAYPART_PRESET_I18N,
  daypartPreset,
  DaypartPreset
} from './availabilityDayparts';
import styles from './deviceManagement.module.scss';

/** Formik 欄位名。這一段只有一個欄位，名字不外流。 */
const DAYPART_FIELD = 'dayparts';

type DaypartFormValues = { [key: string]: DaypartsValue };

/**
 * 複製一份時段表。
 *
 * `Dayparts` 在切換單一格子時是對傳進去的小時陣列直接 `splice`，所以進出各複製一次：
 * 進去的那一份若是上層的 state，會被就地改掉；出來的那一份若不複製，上層存起來之後
 * 還會繼續被改，下一次比較新舊條件時就會看成沒變而不重查。
 */
export function cloneDayparts(value: DaypartsValue): DaypartsValue {
  return DAYPART_DAYS.reduce(
    (daypart, day) => ({ ...daypart, [day]: [...(value[day] ?? [])] }),
    { enabled: true } as DaypartsValue
  );
}

const DaypartsWatcher: React.FC<{
  value: DaypartsValue;
  onChange: (value: DaypartsValue) => void;
}> = ({ value, onChange }) => {
  const previous = useRef(value);
  useEffect(() => {
    if (_.isEqual(previous.current, value)) {
      return;
    }
    previous.current = cloneDayparts(value);
    onChange(cloneDayparts(value));
  }, [value, onChange]);
  return null;
};

type AvailabilityDaypartsFieldProps = {
  value: DaypartsValue;
  onChange: (value: DaypartsValue) => void;
};

/**
 * 機台可售狀況的投放時段。
 *
 * 用的是活動設定、PMP、Beacon 訊息共用的那一支 `Dayparts`，格線、上午下午的欄頭、
 * 整列整欄的切換與快速選項都在那邊。它吃的是 Formik 的 context 型別，所以這裡就地
 * 開一個 Formik 當容器 —— 這一頁沒有表單，時段也不會被送出，容器只是為了把
 * 既有元件接上來，而不是另外做一份格線。
 *
 * 不用 `segmentOnly`：那個模式是電子紙的四段式，這一頁要的是逐小時。
 */
export const AvailabilityDaypartsField: React.FC<
  AvailabilityDaypartsFieldProps
> = ({ value, onChange }) => {
  // 只在掛載時取一次：`Dayparts` 也只在 componentDidMount 讀一次 props，
  // 之後由它自己持有，這裡跟著它的節奏才不會兩邊互相覆蓋。
  const initialValues = useMemo<DaypartFormValues>(
    () => ({ [DAYPART_FIELD]: cloneDayparts(value) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 快捷改的是整張表,而 `Dayparts` 只在掛載時讀一次 props、之後自己持有狀態 ——
  // 所以光把 Formik 的值換掉,格線不會跟著動。換一個 key 讓它重新掛載才看得到。
  const [gridKey, setGridKey] = useState(0);

  const renderForm = useCallback(
    (formikProps: FormikProps<DaypartFormValues>) => (
      <>
        {/* 格線與已選時數包在同一層，兩者才是上下排。少了它，兩者會各自成為外層
            橫列的項目，已選時數就會跑到格線右邊。 */}
        <div className={styles.daypartsField}>
          <Dayparts key={gridKey} {...formikProps} name={DAYPART_FIELD} />
          {/* 已選幾小時算在這一層，不動共用元件：格線是所有用到時段的畫面共用的，
              而「一週選了幾小時」只有這一頁要看。 */}
          <div className={styles.daypartSummary} data-testid='daypart-summary'>
            <span>
              {i18n.t('deviceManagement.labels.selectedHoursPerWeek', {
                hours: countSelectedHours(formikProps.values[DAYPART_FIELD])
              })}
            </span>
            {Object.values(DaypartPreset).map((preset) => (
              <span
                key={preset}
                className={styles.daypartPreset}
                data-testid={`daypart-preset-${preset}`}
                onClick={() => {
                  formikProps.setFieldValue(
                    DAYPART_FIELD,
                    daypartPreset(preset)
                  );
                  setGridKey((previous) => previous + 1);
                }}
              >
                {i18n.t(DAYPART_PRESET_I18N[preset])}
              </span>
            ))}
          </div>
        </div>
        <DaypartsWatcher
          value={formikProps.values[DAYPART_FIELD]}
          onChange={onChange}
        />
      </>
    ),
    [onChange, gridKey]
  );

  return (
    <Formik initialValues={initialValues} onSubmit={_.noop}>
      {renderForm}
    </Formik>
  );
};
